// src/components/Details.jsx

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { Heading, Spinner } from '@design-systems/canvas-ui'
import { logEvent, LogLevel } from '@praxis/component-logging'
import { useAuth } from '@praxis/component-auth'
import '../css/header.css'
import '../css/Details.css'
import '../css/common.css'
import {
  loadTMAuthContext,
  clearTMAuthContext,
  loadCaptivePortalContext,
  clearCaptivePortalContext,
  buildSuccessUrl,
  saveTMAuthContext,
  saveCaptivePortalContext,
  isSafeRedirectUrl,
} from '../utils/tmAuthStorage'
import {
  submitTeamMemberStatus,
  AuthenticationError,
  extractAccessToken,
} from '../utils/guestwifiApi'
import { TEAM_MEMBER_STATUS_PATH } from '../utils/apiRoutes'

const Alert = ({ open, severity, children }) => {
  if (!open) return null
  return <div className="details-success-alert">{children}</div>
}

function hasHydratedSession(session) {
  return !!(session?.userInfo && Object.keys(session.userInfo).length > 0)
}

const CALLBACK_SESSION_TIMEOUT_MS = 30000

function pickFirstNonEmpty(...values) {
  return (
    values.find(
      (value) => value !== undefined && value !== null && value !== '',
    ) || null
  )
}

function normalizeMacAddress(macAddress) {
  return macAddress?.trim().toLowerCase() || null
}

const Details = ({ config, setAuthenticated }) => {
  const location = useLocation()
  const isCallbackRoute = location.pathname === '/auth/login'

  const routeState = useMemo(() => location.state || {}, [location.state])

  const storedTMState = useMemo(() => loadTMAuthContext() || {}, [])
  const storedPortalState = useMemo(() => loadCaptivePortalContext() || {}, [])

  const state = useMemo(() => {
    return { ...storedPortalState, ...storedTMState, ...routeState }
  }, [storedPortalState, storedTMState, routeState])

  const isProd = config?.environment === 'production'
  const runtimeConfig = config

  const searchParams = new URLSearchParams(window.location.search)

  const flowId = pickFirstNonEmpty(
    state.flowId,
    searchParams.get('flow_id'),
    storedTMState.flowId,
    storedPortalState.flowId,
  )

  const mac = normalizeMacAddress(
    pickFirstNonEmpty(
      searchParams.get('client_mac'),
      searchParams.get('mac_address'),
      routeState.mac,
      storedTMState.mac,
      storedPortalState.mac,
      !isProd ? runtimeConfig?.testMac : null,
    ),
  )

  const loc =
    pickFirstNonEmpty(
      state.loc,
      searchParams.get('location'),
      storedTMState.loc,
      storedPortalState.loc,
    ) || 'local'

  const deviceType =
    pickFirstNonEmpty(
      state.deviceType,
      searchParams.get('device_type'),
      storedTMState.deviceType,
      storedPortalState.deviceType,
      window.navigator.userAgent,
    ) || 'UNKNOWN'

  const baseGrantUrl = pickFirstNonEmpty(
    state.baseGrantUrl,
    searchParams.get('base_grant_url'),
    storedTMState.baseGrantUrl,
    storedPortalState.baseGrantUrl,
  )

  const userContinueUrl = pickFirstNonEmpty(
    state.userContinueUrl,
    searchParams.get('user_continue_url'),
    storedTMState.userContinueUrl,
    storedPortalState.userContinueUrl,
  )

  const successUrl =
    state.successUrl || buildSuccessUrl(baseGrantUrl, userContinueUrl) || null

  const { session, login } = useAuth()

  const userInfo = session?.userInfo || {}
  const lanId = userInfo.lanId || userInfo.eid || ''
  const email = userInfo.email || ''

  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [success, setSuccess] = useState(false)

  const hasApprovedSubmittedRef = useRef(false)
  const hasLoginTriggeredRef = useRef(false)
  const hasInProgressSubmittedRef = useRef(false)
  const hasTerminalStatusRef = useRef(false)

  const authError = searchParams.get('error')
  const authErrorDescription = searchParams.get('error_description')

  const updateTMContextStep = useCallback(
    (step, extra = {}) => {
      if (!flowId) return

      const current = loadTMAuthContext() || {}
      saveTMAuthContext({
        ...current,
        ...state,
        ...extra,
        flowId,
        mac,
        loc,
        deviceType,
        baseGrantUrl,
        userContinueUrl,
        successUrl,
        step,
        terminalStatus:
          extra?.terminalStatus ?? current?.terminalStatus ?? false,
      })
    },
    [
      flowId,
      state,
      mac,
      loc,
      deviceType,
      baseGrantUrl,
      userContinueUrl,
      successUrl,
    ],
  )

  const submitTMData = useCallback(
    async (status, accessToken = null) => {
      if (!flowId) {
        throw new Error('Missing flow id in team-member flow context')
      }

      if (!mac) {
        throw new Error('Missing MAC address in team-member flow context')
      }

      // For Approved status, use the new helper that handles bearer token attachment
      if (status === 'Approved') {
        return submitTeamMemberStatus({
          apiUrl: TEAM_MEMBER_STATUS_PATH,
          status,
          flowId,
          macAddress: mac,
          location: loc,
          deviceType,
          accessToken,
        })
      }

      // For non-approved statuses (In Progress, Rejected, Time-Out), use legacy path
      // These do not require a bearer token
      const payload = {
        flow_id: flowId,
        mac_address: mac,
        status,
        location: loc,
        device_type: deviceType,
      }

      logEvent(
        {
          message: `tm_status flowId=${flowId} mac=${mac} status=${status}`,
        },
        { level: LogLevel.Info },
      )

      const response = await fetch(TEAM_MEMBER_STATUS_PATH, {
        method: 'POST',
        keepalive: status !== 'Approved',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        throw new Error(`TM status update failed: ${response.status}`)
      }

      const data = await response.json()
      if (data?.status !== 'success') {
        throw new Error(data?.message || 'Backend update failed')
      }

      return data
    },
    [flowId, mac, loc, deviceType],
  )

  const persistTerminalStatusOnce = useCallback(
    (status, userFacingMessage) => {
      if (hasTerminalStatusRef.current) return

      hasTerminalStatusRef.current = true
      setLoading(false)
      setErrorMessage(userFacingMessage)
      updateTMContextStep(status, {
        lastError: userFacingMessage,
        terminalStatus: true,
      })

      if (!flowId || !mac) {
        console.error(
          '[SSO] Cannot persist terminal TM status because flow_id or MAC is missing',
          {
            status,
            flowId,
            mac,
          },
        )
        return
      }

      submitTMData(status).catch((error) => {
        console.error(`[SSO] Failed to persist TM status ${status}`, error)
      })
    },
    [flowId, mac, submitTMData, updateTMContextStep],
  )

  useEffect(() => {
    if (!flowId || !mac || !baseGrantUrl || !userContinueUrl || !successUrl) {
      return
    }

    const restoredContext = {
      flowId,
      mac,
      baseGrantUrl,
      userContinueUrl,
      loc,
      deviceType,
      successUrl,
    }

    saveTMAuthContext({
      ...restoredContext,
      step: storedTMState?.step || 'start',
    })
    saveCaptivePortalContext(restoredContext)
  }, [
    flowId,
    mac,
    baseGrantUrl,
    userContinueUrl,
    loc,
    deviceType,
    successUrl,
    storedTMState?.step,
  ])

  useEffect(() => {
    if (!authError) return

    persistTerminalStatusOnce(
      'Rejected',
      `Authentication failed: ${authErrorDescription || authError}. Please restart the Team Member flow.`,
    )
  }, [authError, authErrorDescription, persistTerminalStatusOnce])

  useEffect(() => {
    const hasSession = hasHydratedSession(session)

    if (
      isCallbackRoute ||
      hasSession ||
      hasLoginTriggeredRef.current ||
      authError
    ) {
      return
    }

    if (!flowId) {
      setErrorMessage('Missing flow id. Please restart the Team Member flow.')
      return
    }

    if (!mac) {
      setErrorMessage(
        'Unable to continue because the captive portal MAC context is missing.',
      )
      return
    }

    if (
      loadTMAuthContext()?.terminalStatus ||
      loadCaptivePortalContext()?.terminalStatus
    ) {
      setErrorMessage(
        'This session has already ended. Please restart the Team Member flow.',
      )
      clearTMAuthContext()
      clearCaptivePortalContext()
      return
    }

    hasLoginTriggeredRef.current = true
    setLoading(true)
    updateTMContextStep('login_started')

    const startLogin = async () => {
      if (hasTerminalStatusRef.current) return

      try {
        if (!hasInProgressSubmittedRef.current) {
          hasInProgressSubmittedRef.current = true
          updateTMContextStep('in_progress')
          await submitTMData('In Progress')
        }
      } catch (error) {
        console.error('[TM] submitTMData(In Progress) failed', error)
        persistTerminalStatusOnce(
          'Rejected',
          `Unable to reach team-member service: ${error?.message || 'network error'}. Please restart the Team Member flow.`,
        )
        return
      }

      try {
        await Promise.resolve(login())
      } catch (error) {
        console.error('[TM] login() failed', error)
        persistTerminalStatusOnce(
          'Rejected',
          `Unable to start SSO login: ${error?.message || 'network error'}. Please restart the Team Member flow.`,
        )
      } finally {
        setLoading(false)
      }
    }

    startLogin()
  }, [
    isCallbackRoute,
    session,
    authError,
    login,
    mac,
    flowId,
    submitTMData,
    persistTerminalStatusOnce,
    updateTMContextStep,
  ])

  useEffect(() => {
    const hasSession = hasHydratedSession(session)

    if (!isCallbackRoute) return

    if (hasSession || authError) {
      setLoading(false)
      return
    }

    setLoading(true)
    updateTMContextStep('callback_pending')

    const timer = window.setTimeout(() => {
      persistTerminalStatusOnce(
        'Time-Out',
        'SSO session could not be established. Please try again.',
      )
    }, CALLBACK_SESSION_TIMEOUT_MS)

    return () => window.clearTimeout(timer)
  }, [
    isCallbackRoute,
    session,
    authError,
    persistTerminalStatusOnce,
    updateTMContextStep,
  ])

  useEffect(() => {
    if (hasApprovedSubmittedRef.current) return
    if (hasTerminalStatusRef.current) return
    if (!hasHydratedSession(session)) return

    if (!mac) {
      persistTerminalStatusOnce(
        'Rejected',
        'Unable to complete authorization because MAC context is missing.',
      )
      return
    }

    if (!successUrl) {
      persistTerminalStatusOnce(
        'Rejected',
        'Unable to complete authorization because captive portal redirect context is missing.',
      )
      return
    }

    if (!lanId || !email) {
      persistTerminalStatusOnce(
        'Rejected',
        'Authenticated user information is incomplete. Please contact support.',
      )
      return
    }

    // Extract IAM access token from authenticated session
    // Required for Approved status per backend contract
    const accessToken = extractAccessToken(session)
    if (!accessToken) {
      persistTerminalStatusOnce(
        'Rejected',
        'Your authentication session is incomplete. Please sign in again.',
      )
      return
    }

    hasApprovedSubmittedRef.current = true
    hasTerminalStatusRef.current = true
    setLoading(true)
    updateTMContextStep('approved_pending')

    // Submit Approved status with IAM bearer token
    submitTMData('Approved', accessToken)
      .then(() => {
        // N-H1: re-validate successUrl loaded from storage before redirecting
        if (!isSafeRedirectUrl(successUrl)) {
          logEvent(
            { message: `[TM] Blocked unsafe redirect target: ${successUrl}` },
            { level: LogLevel.Error },
          )
          setErrorMessage(
            'Redirect target is not allowed. Please reconnect to Wi-Fi and try again.',
          )
          hasApprovedSubmittedRef.current = false
          return
        }

        setSuccess(true)
        setAuthenticated(true)

        // Mark terminal so a resumed session cannot resend Approved for this flow
        updateTMContextStep('approved_success', { terminalStatus: true })

        clearTMAuthContext()
        clearCaptivePortalContext()

        window.setTimeout(() => {
          window.location.replace(successUrl)
        }, 1500)
      })
      .catch((error) => {
        // Handle 401 (expired/invalid token) as explicit auth failure
        if (error instanceof AuthenticationError && error.statusCode === 401) {
          console.error(
            '[TM] Authorization failed - token expired or invalid',
            error,
          )
          persistTerminalStatusOnce(
            'Rejected',
            error.message ||
              'Your sign-in session expired. Please sign in again.',
          )
          return
        }

        console.error('[SSO] Submission failed', error)
        setErrorMessage(error.message)
        hasApprovedSubmittedRef.current = false
        hasTerminalStatusRef.current = false
        updateTMContextStep('approved_failed', { lastError: error.message })
      })
      .finally(() => setLoading(false))
  }, [
    session,
    mac,
    successUrl,
    lanId,
    email,
    submitTMData,
    setAuthenticated,
    persistTerminalStatusOnce,
    updateTMContextStep,
  ])

  return (
    <div className="details-container">
      <Heading className="details-heading">Team Member Authentication</Heading>

      {loading && (
        <div className="details-loading">
          <Spinner size={24} />
          <span>Authenticating...</span>
        </div>
      )}

      {errorMessage && <div className="details-error">{errorMessage}</div>}

      <Alert open={success} severity="success">
        Successfully Authorized
      </Alert>
    </div>
  )
}

export default Details
